import * as Sentry from "@sentry/react";
import { SearchError } from "../errors/sentryErrors";

import { API_URL as API_BASE } from "../config";
const VISA_PORTAL_BASE = import.meta.env.VITE_VISA_URL;
const GEOAPIFY_KEY = import.meta.env.VITE_GEOAPIFY_API_KEY;

const COUNTRY_LIST_CACHE_KEY = "visa.activeCountries.v1";
const COUNTRY_LIST_TTL_MS = 60 * 60 * 1000;
const GEOCODE_CACHE_KEY = "visa.geocode.v1";
const GEOCODE_TTL_MS = 24 * 60 * 60 * 1000;

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]/g, "");

const readCache = (key, ttl, { ignoreTtl = false } = {}) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!ignoreTtl && Date.now() - (parsed.savedAt || 0) > ttl) return null;
    return parsed.value;
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
  } catch {
  }
};

const ensureNormalized = (country) => {
  if (!country || typeof country !== "object") return null;
  const name = String(country.name || "").trim();
  const slug = slugify(country.slug || name);
  if (!name || !slug) return null;
  return {
    name,
    slug,
    normalized: country.normalized || normalize(name),
    imageUrl: country.imageUrl || null,
  };
};

export const fetchActiveCountries = async ({ force = false } = {}) => {
  if (!force) {
    const cached = readCache(COUNTRY_LIST_CACHE_KEY, COUNTRY_LIST_TTL_MS);
    if (Array.isArray(cached) && cached.length) return cached;
  }
  if (!API_BASE) return [];

  const url = `${API_BASE.replace(/\/$/, "")}/api/visa/countries${force ? "?force=1" : ""}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { accept: "application/json" },
      credentials: "include",
      signal: AbortSignal.timeout(10000),
    });
    if (!response.ok) {
      throw new Error(`Visa country list request failed: ${response.status}`);
    }
    const payload = await response.json();
    const raw = Array.isArray(payload?.countries) ? payload.countries : [];
    const countries = raw.map(ensureNormalized).filter(Boolean);
    if (countries.length) writeCache(COUNTRY_LIST_CACHE_KEY, countries);
    return countries;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "search");
      scope.setTag("app", "web");
      scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
      Sentry.captureException(new SearchError(error instanceof Error ? error.message : String(error)));
    });
    // Network hiccup or backend outage — fall back to whatever we last had
    // cached, however stale, rather than breaking the whole visa flow.
    const stale = readCache(COUNTRY_LIST_CACHE_KEY, COUNTRY_LIST_TTL_MS, { ignoreTtl: true });
    if (Array.isArray(stale) && stale.length) return stale;
    throw error;
  }
};

const ALIASES = {
  "uae": ["unitedarabemirates"],
  "unitedarabemirates": ["uae"],
  "usa": ["unitedstates", "unitedstatesofamerica", "us"],
  "unitedstates": ["usa", "us", "unitedstatesofamerica"],
  "uk": ["unitedkingdom", "greatbritain", "britain"],
  "unitedkingdom": ["uk", "greatbritain", "britain"],
  "southkorea": ["korea", "republicofkorea"],
  "korea": ["southkorea", "republicofkorea"],
  "czechrepublic": ["czechia"],
  "czechia": ["czechrepublic"],
};

const expandAliases = (norm) => {
  const set = new Set([norm]);
  (ALIASES[norm] || []).forEach((a) => set.add(a));
  return set;
};

export const matchCountryInList = (countryName, countries) => {
  if (!countryName || !Array.isArray(countries) || !countries.length) return null;
  const target = normalize(countryName);
  if (!target) return null;
  const aliases = expandAliases(target);

  for (const country of countries) {
    if (aliases.has(country.normalized)) return country;
  }
  for (const country of countries) {
    for (const alias of aliases) {
      if (country.normalized.includes(alias) || alias.includes(country.normalized)) {
        return country;
      }
    }
  }
  return null;
};

export const findCountryInText = (text, countries) => {
  if (!text || !Array.isArray(countries) || !countries.length) return null;
  const haystack = normalize(text);
  if (!haystack) return null;
  let best = null;
  for (const country of countries) {
    if (haystack.includes(country.normalized)) {
      if (!best || country.normalized.length > best.normalized.length) {
        best = country;
      }
    }
  }
  return best;
};

const geocodeCacheGet = (key) => {
  const all = readCache(GEOCODE_CACHE_KEY, GEOCODE_TTL_MS);
  if (!all || typeof all !== "object") return undefined;
  return all[key];
};

const geocodeCacheSet = (key, value) => {
  const all = readCache(GEOCODE_CACHE_KEY, GEOCODE_TTL_MS) || {};
  all[key] = value;
  writeCache(GEOCODE_CACHE_KEY, all);
};

export const geocodeDestinationCountry = async (destination) => {
  if (!destination || !GEOAPIFY_KEY) return null;
  const cacheKey = normalize(destination);
  if (!cacheKey) return null;

  const cached = geocodeCacheGet(cacheKey);
  if (cached !== undefined) return cached || null;

  try {
    const url = `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(
      destination
    )}&format=json&limit=1&apiKey=${GEOAPIFY_KEY}`;
    const response = await fetch(url);
    if (!response.ok) {
      geocodeCacheSet(cacheKey, null);
      return null;
    }
    const data = await response.json();
    const country = data?.results?.[0]?.country || null;
    geocodeCacheSet(cacheKey, country);
    return country;
  } catch {
    return null;
  }
};

export const resolveTripVisaCountry = async (tripData, countries) => {
  if (!Array.isArray(countries) || !countries.length) return null;

  const fromData = tripData?.destinationData?.country;
  if (fromData) {
    const m = matchCountryInList(fromData, countries);
    if (m) return m;
  }

  const destination = tripData?.destination;
  if (destination) {
    const inText = findCountryInText(destination, countries);
    if (inText) return inText;

    const geocoded = await geocodeDestinationCountry(destination);
    if (geocoded) {
      const m = matchCountryInList(geocoded, countries);
      if (m) return m;
    }
  }

  return null;
};

export const buildVisaUrl = (slug) => {
  if (!slug || !VISA_PORTAL_BASE) return null;
  return `${VISA_PORTAL_BASE.replace(/\/$/, "")}/visa/${slug}`;
};

export const resolveDestinationVisa = async (destName, countries) => {
  if (!destName || !Array.isArray(countries) || !countries.length) return null;
  const inText = findCountryInText(destName, countries);
  if (inText) return inText;
  const geocoded = await geocodeDestinationCountry(destName);
  if (geocoded) return matchCountryInList(geocoded, countries);
  return null;
};
